import React, { createContext, useContext, useReducer, useCallback, type ReactNode } from 'react';
import type { WindowConfig, WindowAction, AppType, Position, Size, SnapZone, ArrangementType } from '../types';
import { DEFAULT_WINDOW_SIZE, DEFAULT_MIN_SIZE, APP_TITLES, TASKBAR_HEIGHT, APP_WINDOW_SIZES } from '../utils/constants';
import { generateId, getCascadedPosition } from '../utils/helpers';

// Calculate snap position and size for a given zone
const getSnapDimensions = (zone: SnapZone): { position: Position; size: Size } => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight - TASKBAR_HEIGHT;

  switch (zone) {
    case 'left':
      return {
        position: { x: 0, y: 0 },
        size: { width: screenWidth / 2, height: screenHeight },
      };
    case 'right':
      return {
        position: { x: screenWidth / 2, y: 0 },
        size: { width: screenWidth / 2, height: screenHeight },
      };
    case 'maximize':
      return {
        position: { x: 0, y: 0 },
        size: { width: screenWidth, height: screenHeight },
      };
    default:
      return {
        position: { x: 0, y: 0 },
        size: DEFAULT_WINDOW_SIZE,
      };
  }
};

interface WindowContextType {
  windows: WindowConfig[];
  openWindow: (appType: AppType, options?: Partial<WindowConfig>) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  updatePosition: (id: string, position: Position) => void;
  updateSize: (id: string, size: Size) => void;
  getActiveWindow: () => WindowConfig | null;
  snapWindow: (id: string, zone: SnapZone, savedPosition?: Position, savedSize?: Size) => void;
  arrangeWindows: (type: ArrangementType) => void;
}

const WindowContext = createContext<WindowContextType | null>(null);

// Get next z-index
const getNextZIndex = (windows: WindowConfig[]): number => {
  if (windows.length === 0) return 100;
  return Math.max(...windows.map(w => w.zIndex)) + 1;
};

// Window reducer
const windowReducer = (state: WindowConfig[], action: WindowAction): WindowConfig[] => {
  switch (action.type) {
    case 'OPEN_WINDOW': {
      const { component, ...options } = action.payload;
      const isMobile = window.innerWidth < 768;

      // Check if window already exists
      const existing = state.find(w => w.component === component);
      if (existing) {
        // Focus existing window - on mobile, also ensure maximized
        return state.map(w => ({
          ...w,
          isActive: w.id === existing.id,
          state: w.id === existing.id
            ? (w.state === 'minimized' ? (isMobile ? 'maximized' : 'normal') : (isMobile ? 'maximized' : w.state))
            : w.state,
          zIndex: w.id === existing.id ? getNextZIndex(state) : w.zIndex,
        }));
      }

      // Create new window - use app-specific size if available
      const size = options.size || APP_WINDOW_SIZES[component] || DEFAULT_WINDOW_SIZE;
      const newWindow: WindowConfig = {
        id: generateId(),
        title: options.title || APP_TITLES[component] || 'Window',
        icon: options.icon || component,
        component,
        position: isMobile ? { x: 0, y: 0 } : (options.position || getCascadedPosition(size)),
        size,
        minSize: options.minSize || DEFAULT_MIN_SIZE,
        state: isMobile ? 'maximized' : 'normal',
        zIndex: getNextZIndex(state),
        isActive: true,
        resizable: options.resizable !== false,
        maximizable: options.maximizable !== false,
      };

      // Deactivate all other windows
      const updatedState = state.map(w => ({ ...w, isActive: false }));
      return [...updatedState, newWindow];
    }

    case 'CLOSE_WINDOW': {
      const remaining = state.filter(w => w.id !== action.payload);
      // Activate the topmost remaining window
      if (remaining.length > 0) {
        const topWindow = remaining.reduce((a, b) => a.zIndex > b.zIndex ? a : b);
        return remaining.map(w => ({ ...w, isActive: w.id === topWindow.id }));
      }
      return remaining;
    }

    case 'MINIMIZE_WINDOW': {
      const minimized = state.map(w =>
        w.id === action.payload
          ? { ...w, state: 'minimized' as const, isActive: false }
          : w
      );
      // Activate the next visible window
      const visible = minimized.filter(w => w.state !== 'minimized');
      if (visible.length > 0) {
        const topWindow = visible.reduce((a, b) => a.zIndex > b.zIndex ? a : b);
        return minimized.map(w => ({ ...w, isActive: w.id === topWindow.id }));
      }
      return minimized;
    }

    case 'MAXIMIZE_WINDOW':
      return state.map(w =>
        w.id === action.payload
          ? { ...w, state: 'maximized' as const, isActive: true, zIndex: getNextZIndex(state) }
          : { ...w, isActive: false }
      );

    case 'RESTORE_WINDOW': {
      const isMobileRestore = window.innerWidth < 768;
      return state.map(w =>
        w.id === action.payload
          ? { ...w, state: isMobileRestore ? 'maximized' as const : 'normal' as const, isActive: true, zIndex: getNextZIndex(state) }
          : { ...w, isActive: false }
      );
    }

    case 'FOCUS_WINDOW': {
      const isMobileFocus = window.innerWidth < 768;
      return state.map(w => ({
        ...w,
        isActive: w.id === action.payload,
        zIndex: w.id === action.payload ? getNextZIndex(state) : w.zIndex,
        state: w.id === action.payload && w.state === 'minimized'
          ? (isMobileFocus ? 'maximized' : 'normal')
          : w.state,
      }));
    }

    case 'UPDATE_POSITION':
      return state.map(w =>
        w.id === action.payload.id
          ? { ...w, position: action.payload.position }
          : w
      );

    case 'UPDATE_SIZE':
      return state.map(w =>
        w.id === action.payload.id
          ? { ...w, size: action.payload.size }
          : w
      );

    case 'BRING_TO_FRONT':
      return state.map(w => ({
        ...w,
        zIndex: w.id === action.payload ? getNextZIndex(state) : w.zIndex,
        isActive: w.id === action.payload,
      }));

    case 'SNAP_WINDOW': {
      const { id, zone, savedPosition, savedSize } = action.payload;

      if (!zone) {
        // Unsnap - restore to saved position/size if available
        return state.map(w => {
          if (w.id === id && w.savedPosition && w.savedSize) {
            return {
              ...w,
              position: w.savedPosition,
              size: w.savedSize,
              savedPosition: undefined,
              savedSize: undefined,
              snapZone: undefined,
              state: 'normal' as const,
              isActive: true,
              zIndex: getNextZIndex(state),
            };
          }
          return w.id === id ? { ...w, isActive: true, zIndex: getNextZIndex(state) } : { ...w, isActive: false };
        });
      }

      const snapDimensions = getSnapDimensions(zone);
      return state.map(w => {
        if (w.id === id) {
          return {
            ...w,
            position: snapDimensions.position,
            size: snapDimensions.size,
            savedPosition: savedPosition || w.savedPosition || w.position,
            savedSize: savedSize || w.savedSize || w.size,
            snapZone: zone,
            state: 'normal' as const,
            isActive: true,
            zIndex: getNextZIndex(state),
          };
        }
        return { ...w, isActive: false };
      });
    }

    case 'ARRANGE_WINDOWS': {
      const arrangementType = action.payload;
      const visibleWindows = state.filter(w => w.state !== 'minimized');
      const count = visibleWindows.length;

      if (count === 0) return state;

      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight - TASKBAR_HEIGHT;

      return state.map(w => {
        if (w.state === 'minimized') return w;

        const visibleIndex = visibleWindows.findIndex(vw => vw.id === w.id);
        let newPosition: Position;
        let newSize: Size;

        switch (arrangementType) {
          case 'cascade':
            newPosition = { x: visibleIndex * 30, y: visibleIndex * 30 };
            newSize = { width: Math.min(600, screenWidth - visibleIndex * 30 - 50), height: Math.min(400, screenHeight - visibleIndex * 30 - 50) };
            break;
          case 'tile-horizontal':
            newPosition = { x: 0, y: (screenHeight / count) * visibleIndex };
            newSize = { width: screenWidth, height: screenHeight / count };
            break;
          case 'tile-vertical':
            newPosition = { x: (screenWidth / count) * visibleIndex, y: 0 };
            newSize = { width: screenWidth / count, height: screenHeight };
            break;
          default:
            return w;
        }

        return {
          ...w,
          position: newPosition,
          size: newSize,
          state: 'normal' as const,
          snapZone: undefined,
          savedPosition: undefined,
          savedSize: undefined,
          zIndex: 100 + visibleIndex,
        };
      });
    }

    default:
      return state;
  }
};

interface WindowProviderProps {
  children: ReactNode;
}

export const WindowProvider: React.FC<WindowProviderProps> = ({ children }) => {
  const [windows, dispatch] = useReducer(windowReducer, []);

  const openWindow = useCallback((appType: AppType, options?: Partial<WindowConfig>) => {
    dispatch({ type: 'OPEN_WINDOW', payload: { component: appType, ...options } });
  }, []);

  const closeWindow = useCallback((id: string) => {
    dispatch({ type: 'CLOSE_WINDOW', payload: id });
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    dispatch({ type: 'MINIMIZE_WINDOW', payload: id });
  }, []);

  const maximizeWindow = useCallback((id: string) => {
    dispatch({ type: 'MAXIMIZE_WINDOW', payload: id });
  }, []);

  const restoreWindow = useCallback((id: string) => {
    dispatch({ type: 'RESTORE_WINDOW', payload: id });
  }, []);

  const focusWindow = useCallback((id: string) => {
    dispatch({ type: 'FOCUS_WINDOW', payload: id });
  }, []);

  const updatePosition = useCallback((id: string, position: Position) => {
    dispatch({ type: 'UPDATE_POSITION', payload: { id, position } });
  }, []);

  const updateSize = useCallback((id: string, size: Size) => {
    dispatch({ type: 'UPDATE_SIZE', payload: { id, size } });
  }, []);

  const getActiveWindow = useCallback(() => {
    return windows.find(w => w.isActive) || null;
  }, [windows]);

  const snapWindow = useCallback((id: string, zone: SnapZone, savedPosition?: Position, savedSize?: Size) => {
    dispatch({ type: 'SNAP_WINDOW', payload: { id, zone, savedPosition, savedSize } });
  }, []);

  const arrangeWindows = useCallback((type: ArrangementType) => {
    dispatch({ type: 'ARRANGE_WINDOWS', payload: type });
  }, []);

  return (
    <WindowContext.Provider
      value={{
        windows,
        openWindow,
        closeWindow,
        minimizeWindow,
        maximizeWindow,
        restoreWindow,
        focusWindow,
        updatePosition,
        updateSize,
        getActiveWindow,
        snapWindow,
        arrangeWindows,
      }}
    >
      {children}
    </WindowContext.Provider>
  );
};

export const useWindows = (): WindowContextType => {
  const context = useContext(WindowContext);
  if (!context) {
    throw new Error('useWindows must be used within a WindowProvider');
  }
  return context;
};
